/**
 * Maps Optimizer
 * Provides optimized Google Maps integration with lazy loading, caching, and cost reduction
 */

import mapsCache from './mapsCache';

class MapsOptimizer {
  constructor() {
    this.isInitialized = false;
    this.observers = new Map();
    this.intersectionObserver = null;
    this.debounceTimers = new Map();
  }

  /**
   * Initialize the maps optimizer
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      await mapsCache.waitForGoogleMaps();
      this.setupIntersectionObserver();
      this.setupGlobalErrorHandlers();
      this.isInitialized = true;
    } catch (error) {
    }
  }

  /**
   * Setup global error handlers for DOM manipulation errors
   */
  setupGlobalErrorHandlers() {
    // Handle uncaught DOM manipulation errors
    window.addEventListener('error', (event) => {
      if (event.error && event.error.message && 
          (event.error.message.includes('removeChild') || 
           event.error.message.includes('appendChild') ||
           event.error.message.includes('insertBefore'))) {
        event.preventDefault();
        return false;
      }
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && event.reason.message && 
          (event.reason.message.includes('removeChild') ||
           event.reason.message.includes('appendChild') ||
           event.reason.message.includes('insertBefore'))) {
        event.preventDefault();
      }
    });

    // Override native DOM methods to prevent errors
    this.overrideDOMMethods();
  }

  /**
   * Override DOM methods to prevent errors
   */
  overrideDOMMethods() {
    const originalRemoveChild = Node.prototype.removeChild;
    const originalAppendChild = Node.prototype.appendChild;
    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.removeChild = function(child) {
      try {
        if (this.contains(child)) {
          return originalRemoveChild.call(this, child);
        } else {
          return child;
        }
      } catch (error) {
        return child;
      }
    };

    Node.prototype.appendChild = function(child) {
      try {
        return originalAppendChild.call(this, child);
      } catch (error) {
        return child;
      }
    };

    Node.prototype.insertBefore = function(newNode, referenceNode) {
      try {
        return originalInsertBefore.call(this, newNode, referenceNode);
      } catch (error) {
        return newNode;
      }
    };
  }

  /**
   * Setup intersection observer for lazy loading
   */
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const mapElement = entry.target;
            const mapId = mapElement.dataset.mapId;
            if (mapId && this.observers.has(mapId)) {
              const { callback, options } = this.observers.get(mapId);
              callback(mapElement, options);
              this.intersectionObserver.unobserve(mapElement);
              this.observers.delete(mapId);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading when map is 50px away from viewport
        threshold: 0.1
      }
    );
  }

  /**
   * Lazy load map when it comes into view
   */
  observeMapElement(mapElement, options, callback) {
    if (!this.intersectionObserver) {
      // Fallback: load immediately if intersection observer not supported
      callback(mapElement, options);
      return;
    }

    const mapId = `map_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    mapElement.dataset.mapId = mapId;
    
    this.observers.set(mapId, { callback, options });
    this.intersectionObserver.observe(mapElement);
  }

  /**
   * Create optimized map instance with caching
   */
  async createMap(mapElement, options, forceRecreate = false) {
    // Validate inputs
    if (!mapElement || !options) {
      throw new Error('Invalid map element or options');
    }

    // Check if element is still in DOM
    if (!mapElement.parentNode) {
      throw new Error('Map element is not in DOM');
    }

    // Check if Google Maps API is available
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps API is not loaded. Please check your internet connection.');
    }

    // Check if element already has a map instance (unless forcing recreation)
    if (!forceRecreate && mapElement.dataset.mapInitialized === 'true') {
      return { map: null, markers: [] };
    }

    // If forcing recreation, reset the initialization flag
    if (forceRecreate) {
      mapElement.dataset.mapInitialized = 'false';
    }

    const { center, zoom, venues = [] } = options;
    const cacheKey = mapsCache.generateMapKey(center, zoom, venues);
    

    // Check if we have a cached map configuration (unless forcing recreation)
    if (!forceRecreate && mapsCache.hasMapInstance(cacheKey)) {
      const cachedConfig = mapsCache.getMapInstance(cacheKey);

      // Validate cached config
      if (!cachedConfig || !cachedConfig.center || typeof cachedConfig.zoom !== 'number') {
      } else {
        // Create new map instance with cached configuration
        const mapOptions = {
          center: cachedConfig.center,
          zoom: cachedConfig.zoom,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ],
          gestureHandling: 'cooperative',
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          mapTypeId: 'roadmap'
        };

        try {
          const map = new window.google.maps.Map(mapElement, mapOptions);
          
          // Recreate markers for the new map instance
          const markers = this.createMarkers(map, venues);
          
          // Only mark as initialized after successful creation
          mapElement.dataset.mapInitialized = 'true';
          
          return {
            map: map,
            markers: markers
          };
        } catch (error) {
          // Reset the flag if map creation failed
          mapElement.dataset.mapInitialized = 'false';
        }
      }
    }

    // Create new map instance
    const mapOptions = {
      center,
      zoom,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      gestureHandling: 'cooperative',
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      mapTypeId: 'roadmap'
    };

    try {
      const map = new window.google.maps.Map(mapElement, mapOptions);

      // Track API call
      mapsCache.trackApiCall('Map_Load', { center, zoom });

      // Create markers efficiently
      const markers = this.createMarkers(map, venues);

      // Only mark as initialized after successful creation
      mapElement.dataset.mapInitialized = 'true';

      // Cache the map instance
      mapsCache.setMapInstance(cacheKey, map, markers);

      return { map, markers };
    } catch (error) {
      mapElement.dataset.mapInitialized = 'false';
      throw error;
    }
  }

  /**
   * Create markers efficiently with clustering for large datasets
   */
  createMarkers(map, venues) {
    if (!map || !venues || !Array.isArray(venues) || venues.length === 0) {
      return [];
    }

    const markers = [];
    const maxMarkers = 50; // Limit markers to reduce API calls

    try {
      // If we have too many venues, implement simple clustering
      if (venues.length > maxMarkers) {
        return this.createClusteredMarkers(map, venues, maxMarkers);
      }

      venues.forEach((venue, index) => {
        try {
          if (venue && venue.coordinates && 
              typeof venue.coordinates.lat === 'number' && 
              typeof venue.coordinates.lng === 'number') {
            
            const marker = new window.google.maps.Marker({
              position: venue.coordinates,
              map,
              title: venue.name || `Venue ${index + 1}`,
              label: {
                text: venue.price || '£250/hr',
                className: 'venue-marker-label'
              },
              // Optimize marker rendering
              optimized: true,
              animation: window.google.maps.Animation.DROP
            });

            // Add click listener safely
            if (typeof venue.onClick === 'function') {
              marker.addListener('click', () => {
                try {
                  venue.onClick(venue);
                } catch (error) {
                }
              });
            }

            markers.push(marker);
          }
        } catch (error) {
        }
      });
    } catch (error) {
    }

    return markers;
  }

  /**
   * Create clustered markers for large datasets
   */
  createClusteredMarkers(map, venues, maxMarkers) {
    const markers = [];
    const clusterSize = Math.ceil(venues.length / maxMarkers);
    
    // Group venues by proximity
    const clusters = this.groupVenuesByProximity(venues, clusterSize);
    
    clusters.forEach((cluster, index) => {
      const center = this.calculateClusterCenter(cluster);
      const marker = new window.google.maps.Marker({
        position: center,
        map,
        title: `${cluster.length} venues`,
        label: {
          text: `${cluster.length}`,
          className: 'cluster-marker-label'
        },
        optimized: true
      });

      // Add click listener to show cluster details
      marker.addListener('click', () => {
        this.showClusterInfo(map, cluster, center);
      });

      markers.push(marker);
    });

    return markers;
  }

  /**
   * Group venues by proximity for clustering
   */
  groupVenuesByProximity(venues, clusterSize) {
    const clusters = [];
    const used = new Set();

    venues.forEach((venue, index) => {
      if (used.has(index)) return;

      const cluster = [venue];
      used.add(index);

      // Find nearby venues
      venues.forEach((otherVenue, otherIndex) => {
        if (used.has(otherIndex) || cluster.length >= clusterSize) return;

        const distance = this.calculateDistance(venue.coordinates, otherVenue.coordinates);
        if (distance < 0.01) { // ~1km radius
          cluster.push(otherVenue);
          used.add(otherIndex);
        }
      });

      clusters.push(cluster);
    });

    return clusters;
  }

  /**
   * Calculate distance between two coordinates
   */
  calculateDistance(coord1, coord2) {
    const R = 6371; // Earth's radius in km
    const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
    const dLng = (coord2.lng - coord1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Calculate center of a cluster
   */
  calculateClusterCenter(venues) {
    const lat = venues.reduce((sum, venue) => sum + venue.coordinates.lat, 0) / venues.length;
    const lng = venues.reduce((sum, venue) => sum + venue.coordinates.lng, 0) / venues.length;
    return { lat, lng };
  }

  /**
   * Show cluster information
   */
  showClusterInfo(map, venues, center) {
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px;">
          <h3>${venues.length} Venues Found</h3>
          <div style="max-height: 200px; overflow-y: auto;">
            ${venues.map(venue => `
              <div style="padding: 5px 0; border-bottom: 1px solid #eee;">
                <strong>${venue.name}</strong><br>
                <small>${venue.price || '£250/hr'}</small>
              </div>
            `).join('')}
          </div>
        </div>
      `,
      maxWidth: 300
    });

    infoWindow.setPosition(center);
    infoWindow.open(map);
  }

  /**
   * Debounced map update to prevent excessive API calls
   */
  debouncedUpdateMap(mapId, updateFunction, delay = 300) {
    if (this.debounceTimers.has(mapId)) {
      clearTimeout(this.debounceTimers.get(mapId));
    }

    const timer = setTimeout(() => {
      updateFunction();
      this.debounceTimers.delete(mapId);
    }, delay);

    this.debounceTimers.set(mapId, timer);
  }

  /**
   * Optimize map for mobile devices
   */
  optimizeForMobile(map) {
    if (window.innerWidth <= 768) {
      map.setOptions({
        gestureHandling: 'greedy',
        zoomControl: false,
        fullscreenControl: false
      });
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.observers.clear();
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();
    
    // Reset all map elements
    document.querySelectorAll('[data-map-initialized="true"]').forEach(element => {
      element.dataset.mapInitialized = 'false';
    });
    
    mapsCache.clearAllCache();
  }

  /**
   * Get optimization statistics
   */
  getStats() {
    return {
      apiCalls: mapsCache.getApiStats(),
      cachedMaps: mapsCache.mapInstances.size,
      activeObservers: this.observers.size
    };
  }
}

// Create singleton instance
const mapsOptimizer = new MapsOptimizer();

export default mapsOptimizer;
